import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { DEFAULT_DCUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { DCUIContent, DCUITheme, MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import {
    DCUI_CONTENT_PROPERTY_TYPE_ID,
    DCUI_THEME_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { ActiveAcuantPropertyFlags } from "@idw-types/common/active-acuant-property-flags";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { DCUIThemingValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { getUpdatedPropertiesForContentPropertyUpdate } from "@utils/content-util";
import { DCUIThemingThemeTabContainer } from "./dcui-theming-theme-tab-container/dcui-theming-theme-tab-container";
import { DCUIThemingStartPageTabContainer } from "./dcui-theming-start-page-tab-container/dcui-theming-start-page-tab-container";
import { DCUIThemingErrorTabContainer } from "./dcui-theming-error-tab-container/dcui-theming-error-tab-container";
import { DCUIThemingCompleteTabContainer } from "./dcui-theming-complete-container/dcui-theming-complete-container";
import { DCUIThemingStepTabContainer } from "./dcui-theming-step-tab-container/dcui-theming-step-tab-container";
import { ThemingSubTabContainer } from "../../../common/theming-sub-tab-container/theming-sub-tab-container";
import { DCUIThemingTextContentTabContainer } from "./dcui-theming-text-content-tab-container/dcui-theming-text-content-tab-container";
import { DCUIThemingCustomCssTabContainer } from "./dcui-theming-custom-css-tab-container/dcui-theming-custom-css-tab-container";
import { useMemo } from "react";

type VerificationServiceDetailsDCUIThemingTabProps = {
    isDisabled: boolean;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    multiLanguageDCUIContent: MultiLanguageWrapper<DCUIContent>;
    validationErrors: DCUIThemingValidationErrors;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    inheritedDCUIContent: DCUIContent;
    inheritedDCUITheme: DCUITheme;
    documentCaptureSteps: Array<DCUIThemingStep>;
    activeAcuantProperties: ActiveAcuantPropertyFlags;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
};

export const VerificationServiceDetailsDCUIThemingTab = (props: VerificationServiceDetailsDCUIThemingTabProps) => {
    const {
        isDisabled,
        selectedLanguage,
        isMultiLanguageEnabled,
        availableLanguages,
        multiLanguageDCUIContent,
        validationErrors,
        dcuiContent,
        dcuiTheme,
        inheritedDCUIContent,
        inheritedDCUITheme,
        documentCaptureSteps,
        activeAcuantProperties,
        rpServiceConfigurationState,
        setRpServiceConfigurationState,
        uploadFileToS3
    } = props;
   
    const permissionBasedIsDisabled = useMemo(() => {
        return (
            isDisabled ||
            !rpServiceConfigurationState.permissions.canModifyDCUICustomization ||
            !rpServiceConfigurationState.permissions.canModifyBranding
        );
    }, [
        isDisabled,
        rpServiceConfigurationState.permissions.canModifyDCUICustomization,
        rpServiceConfigurationState.permissions.canModifyBranding
    ]);

    const updateDCUIContentPropertyValue = (newDcuiContent: DCUIContent) => {
        const newProperties = getUpdatedPropertiesForContentPropertyUpdate(
            DCUI_CONTENT_PROPERTY_TYPE_ID,
            rpServiceConfigurationState.properties,
            newDcuiContent,
            multiLanguageDCUIContent,
            availableLanguages,
            selectedLanguage,
            isMultiLanguageEnabled,
            DEFAULT_DCUI_CONTENT
        );
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const updateDCUIThemePropertyValue = (dcuiTheme: DCUITheme) => {
        const newValue = JSON.stringify(dcuiTheme);
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            if (property.propertyTypeId === DCUI_THEME_PROPERTY_TYPE_ID) {
                return {
                    ...property,
                    value: newValue,
                    addedToService: true
                };
            } else {
                return property;
            }
        });
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    return (
        <ThemingSubTabContainer>
            <Tabs id="document-capture" unmountOnExit={true}>
                <Tab eventKey={DCUIThemingTabTypes.THEME} title={validationErrors.theme.tabTitle}>
                    <DCUIThemingThemeTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.theme}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        inheritedDCUIContent={inheritedDCUIContent}
                        inheritedDCUITheme={inheritedDCUITheme}
                        pageLevel={PageLevelTypes.APIKEY}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.VERIFICATION_SERVICE}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                        updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                        uploadFileToS3={uploadFileToS3}
                    />
                </Tab>
                <Tab eventKey={DCUIThemingTabTypes.START_PAGE} title={validationErrors.startPage.tabTitle}>
                    <DCUIThemingStartPageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.startPage}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        inheritedDCUIContent={inheritedDCUIContent}
                        pageLevel={PageLevelTypes.APIKEY}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.VERIFICATION_SERVICE}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                        uploadFileToS3={uploadFileToS3}
                    />
                </Tab>
                {documentCaptureSteps.map(step => (
                    <Tab eventKey={step.tabType} title={validationErrors[step.stepType].tabTitle} key={step.tabType}>
                        <DCUIThemingStepTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors[step.stepType]}
                            dcuiContent={dcuiContent}
                            inheritedDCUIContent={inheritedDCUIContent}
                            pageLevel={PageLevelTypes.APIKEY}
                            dcuiTheme={dcuiTheme}
                            tabType={step.tabType}
                            stepType={step.stepType}
                            stepResources={step.stepResources}
                            documentCaptureSteps={documentCaptureSteps}
                            previewEntityType={PreviewEntityTypes.VERIFICATION_SERVICE}
                            updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                            uploadFileToS3={uploadFileToS3}
                        />
                    </Tab>
                ))}
                <Tab eventKey={DCUIThemingTabTypes.CAPTURE_COMPLETE} title={validationErrors.captureComplete.tabTitle}>
                    <DCUIThemingCompleteTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.captureComplete}
                        dcuiContent={dcuiContent}
                        inheritedDCUIContent={inheritedDCUIContent}
                        pageLevel={PageLevelTypes.APIKEY}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.VERIFICATION_SERVICE}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                    />
                </Tab>
                <Tab eventKey={DCUIThemingTabTypes.ERROR} title={validationErrors.errorPage.tabTitle}>
                    <DCUIThemingErrorTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.errorPage}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        inheritedDCUIContent={inheritedDCUIContent}
                        pageLevel={PageLevelTypes.APIKEY}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={PreviewEntityTypes.VERIFICATION_SERVICE}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                    />
                </Tab>
                <Tab eventKey={DCUIThemingTabTypes.CUSTOM_CSS} title={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.customCss.tabName}>
                    <DCUIThemingCustomCssTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        inheritedDCUITheme={inheritedDCUITheme}
                        documentCaptureSteps={documentCaptureSteps}
                        pageLevel={PageLevelTypes.APIKEY}
                        previewEntityType={PreviewEntityTypes.VERIFICATION_SERVICE}
                        updateDCUIThemePropertyValue={updateDCUIThemePropertyValue}
                    />
                </Tab>
                <Tab eventKey={DCUIThemingTabTypes.TEXT_CONTENT} title={validationErrors.textContent.tabTitle}>
                    <DCUIThemingTextContentTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.textContent}
                        dcuiContent={dcuiContent}
                        inheritedDCUIContent={inheritedDCUIContent}
                        pageLevel={PageLevelTypes.APIKEY}
                        activeAcuantProperties={activeAcuantProperties}
                        updateDCUIContentPropertyValue={updateDCUIContentPropertyValue}
                    />
                </Tab>
            </Tabs>
        </ThemingSubTabContainer>
    );
};